import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, MapPin, Award } from "lucide-react";
import { PageHeader, Spinner } from "../components/common";
import { RouteCard } from "./Rail";
import { ReportUpdate } from "./RailStation";
import api from "../lib/api";

export default function RailOperator() {
  const { id } = useParams();
  const [op, setOp] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setOp(null);
    api.get(`/rail/operators/${id}`).then((r) => setOp(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="max-w-4xl mx-auto p-10 text-center text-muted-foreground">Operator not found. <Link to="/rail" className="text-primary underline">Back to Rail</Link></div>;
  if (!op) return <Spinner full />;

  return (
    <div data-testid={`rail-operator-page-${op.id}`}>
      <PageHeader eyebrow={`Rail Operator · ${op.country}`} title={op.common_name}
        subtitle={op.description} entityCode={op.country_code} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {op.routes?.length > 0 && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-4">Notable services</h2>
              <div className="grid sm:grid-cols-2 gap-4">{op.routes.map((r) => <RouteCard key={r.id} r={r} />)}</div>
            </section>
          )}
          {op.stations?.length > 0 && (
            <section className="border border-zinc-300 bg-white p-6">
              <h2 className="font-heading font-900 text-xl mb-4">Major stations served</h2>
              <div className="grid sm:grid-cols-2 gap-2">
                {op.stations.map((s) => (
                  <Link key={s.id} to={`/rail/stations/${s.id}`} className="flex items-center gap-2 py-1.5 text-sm hover:text-emerald-700">
                    <MapPin className="w-3.5 h-3.5 text-emerald-700 shrink-0" />{s.name} <span className="text-muted-foreground">· {s.city}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
        <aside className="space-y-6">
          <section className="border border-zinc-300 bg-white p-5 text-sm space-y-3">
            <h3 className="font-heading font-700 text-lg">Operator profile</h3>
            <Row label="Official name" value={op.name} />
            <Row label="Country" value={op.country} />
            <Row label="Type" value={op.type} />
            <Row label="Services" value={(op.service_types || []).join(", ").replaceAll("_", "-")} />
            {op.loyalty_program && <Row label="Loyalty program" value={op.loyalty_program} icon={<Award className="w-3.5 h-3.5 text-emerald-700" />} />}
            {op.classes?.length > 0 && <Row label="Classes" value={op.classes.join(", ")} />}
            <a href={op.website} target="_blank" rel="noreferrer" data-testid="operator-website-link"
              className="inline-flex items-center gap-1.5 text-emerald-700 font-700 hover:underline">
              Official website <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <p className="text-xs text-muted-foreground pt-1">Last verified {op.last_verified} · Confidence {op.confidence}</p>
          </section>
          <ReportUpdate entityType="operator" entityId={op.id} />
          <Link to="/rail" className="block text-center border border-zinc-300 bg-white px-4 py-2.5 text-sm font-700 hover:border-emerald-600 transition-colors">← All rail operators</Link>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ label, value, icon }) => (
  <div>
    <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="font-600 capitalize flex items-center gap-1.5">{icon}{value}</div>
  </div>
);
